/**
 * lib/reportPdf.ts
 * ------------------------------------------------------------------
 * LKC student report — A5 booklet (4 pages, reading order 1-2-3-4;
 * the office printer's "booklet" setting folds it).
 *
 * DESIGN: ink-light, like Rainbow's report, but in LKC's colours.
 *   - white pages, NO heavy filled backgrounds
 *   - colour comes from TEXT (LKC blue / gold), hairline rules and
 *     small accents — never large fills (saves toner)
 *   - the only contained ink area is a small blue medallion behind
 *     the crest on the cover, so the logo's white ring stays legible
 *
 *   Page 1  Cover  — logo medallion, school name, student detail
 *                     block (Rainbow-style thin-bordered grid)
 *   Page 2  Inner  — subject boxes: header row
 *                     (Subject | Teacher | CA | Exam | Grade) +
 *                     comment paragraph beneath
 *   Page 3  Inner  — class-teacher & HOY remark boxes + summary
 *                     (overflow flows 2 -> 3; padded to a multiple
 *                      of 4 pages so the booklet folds)
 *   Page 4  Back   — announcements, term dates, grade key table
 *
 * Logo: caller injects a data URL (transparent PNG). If absent a
 * light vector crest is drawn instead.
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import { registerReportFont } from '@/lib/reportFont';
import { SCHOOL, termLabel, termDates, schoolReopens } from '@/lib/school.config';
import {
  GRADE_BOUNDARIES,
  CORE_GRADE_BOUNDARIES,
  gradeColor,
  getFormLevel,
  getPassMark,
  shouldShowGrades,
  termUsesExam,
  isAbsent,
} from '@/lib/grading';

export interface ReportSubjectRow {
  name: string;
  caMark: number | null;
  exam: number | 'ABS' | null;
  termMark: number | null;
  grade: string;
  comment: string;
  teacher?: string;
  /** Grading tier for this subject; absent => extended. Drives whether the
   *  back-cover grade key explains the Core-only F/G grades. */
  scheme?: 'extended' | 'core';
  /** Class mean for THIS subject (same basis as the graded mark: exam in
   *  exam terms, term-mark otherwise). Drives the per-subject mark-vs-average
   *  bar on the redesigned academic page. Absent => no comparator drawn. */
  classAverage?: number;
}

export interface ReportStudent {
  name: string;
  className: string;
  admissionNumber?: string;
  dateOfBirth?: string;
  year: string;
  term: string;
  /** June intake: render as their own Term 1 — CA-only, no exam column,
   *  "Term 1" label — even though data lives under the global term. */
  juneCohort?: boolean;
  subjects: ReportSubjectRow[];
  classTeacherComment: string;
  hodComment: string;
  /** Names shown against the two remark boxes. */
  classTeacherName?: string;
  hodName?: string;
  /** Term attendance totals (assembled from reportAttendance). Used by the
   *  results summary strip. Absent when no register was captured. */
  daysAbsent?: number;
  daysPresent?: number;
  possibleDays?: number;
}

export interface BackPageContent {
  announcementsTitle: string;
  announcements: string;
  schoolReopens: string;
  extra?: string;
}

export const DEFAULT_BACK_PAGE: BackPageContent = {
  announcementsTitle: 'Notices',
  announcements:
    'Parents are reminded to settle all outstanding fees before the start of the new term, and to ensure your child has the full list of required textbooks and stationery.',
  schoolReopens: '',
  extra: 'Thank you for your continued support of your child’s education.',
};

export interface RenderOptions {
  logoDataUrl?: string | null;
  back?: BackPageContent;
  /** Term date-range + reopen-date overrides (from the admin Term Calendar).
   *  Curried with the loaded calendar by the caller; fall back to config. */
  getTermDates?: (term: string) => string;
  getSchoolReopens?: (term: string) => string;
  /**
   * STEP 2: Optional override for the exam column header. Called once
   * per student with (formLevel, term) and expected to return the
   * display title. If absent or returns undefined, the PDF falls back
   * to the hardcoded 'EXAM'. The callback shape (rather than a flat
   * map) lets the caller curry it with useData() configs cleanly.
   */
  getExamTitle?: (
    formLevel: string | null,
    term: string,
  ) => string | undefined;
  /**
   * STEP 3: Optional notice text for cover + back cover. Called once
   * per student with (year, term) and expected to return BOTH blocks;
   * either or both may be empty string ("" === silent absence, no
   * block rendered). Same currying pattern as getExamTitle.
   */
  getReportNotice?: (
    year: string,
    term: string,
  ) => { frontPage: string; backPage: string } | undefined;
}

const C = SCHOOL.colors.rgb;
const A5_W = 148;
const A5_H = 210;

// The report's typeface. Set to the embedded 'Lato' by registerReportFont at
// the start of every build (falls back to 'helvetica' if registration fails).
// Every setFont call below uses this so the whole booklet is one modern sans.
let FONT = 'helvetica';

// palette helpers (ink-light)
const BLUE = C.primary;
const GOLD = C.secondary;
const INK: [number, number, number] = [30, 38, 52]; // near-black, slight blue
const GREY: [number, number, number] = [116, 124, 137];
const RULE: [number, number, number] = [219, 224, 231]; // soft hairline borders
const FAINT: [number, number, number] = [248, 250, 253]; // barely-there strip
const GOLDTINT: [number, number, number] = [251, 243, 222]; // soft gold wash
const GOLDLINE: [number, number, number] = [222, 196, 140]; // gold border
const NAVY_PANEL: [number, number, number] = [18, 38, 74]; // login-style deep navy
const WHITE: [number, number, number] = [255, 255, 255];

function setText(pdf: jsPDF, c: [number, number, number]) {
  pdf.setTextColor(c[0], c[1], c[2]);
}
function setDraw(pdf: jsPDF, c: [number, number, number]) {
  pdf.setDrawColor(c[0], c[1], c[2]);
}
function setFill(pdf: jsPDF, c: [number, number, number]) {
  pdf.setFillColor(c[0], c[1], c[2]);
}

/* ---------------- page 1 : cover (white, ink-light) ---------------- */
function cover(pdf: jsPDF, s: ReportStudent, opt: RenderOptions) {
  const cx = A5_W / 2;

  // ── Branded header — navy only around the logo, the rest of the cover
  //    white. Three trial designs (opt.coverStyle); no vertical gold line.
  const placeLogo = (lx: number, ly: number, d: number, fallback: [number, number, number]) => {
    let ok = false;
    if (opt.logoDataUrl) {
      try {
        pdf.addImage(opt.logoDataUrl, 'PNG', lx, ly, d, d);
        ok = true;
      } catch {
        ok = false;
      }
    }
    if (!ok) {
      setText(pdf, fallback);
      pdf.setFont(FONT, 'bold');
      pdf.setFontSize(d * 0.5);
      pdf.text('LKC', lx + d / 2, ly + d * 0.62, { align: 'center' });
    }
  };

  // Layout anchored to the content margin so the navy box + the two-tone
  // accent above it are exactly as wide as the footer's gold line (M..A5_W-M).
  const M = 14;
  const boxW = A5_W - 2 * M;

  // Two-tone accent bar (navy 62% / gold 38%) — mirrors the strip at the top
  // of the live web reports — sitting just above the navy box.
  const accY = 12;
  const accH = 1.8;
  setFill(pdf, NAVY_PANEL);
  pdf.rect(M, accY, boxW * 0.62, accH, 'F');
  setFill(pdf, GOLD);
  pdf.rect(M + boxW * 0.62, accY, boxW * 0.38, accH, 'F');

  // Navy box (square edges) holding the centred logo — the only navy on the
  // cover; everything below is white.
  const boxY = accY + accH + 2.6;
  const boxH = 46;
  setFill(pdf, NAVY_PANEL);
  pdf.rect(M, boxY, boxW, boxH, 'F');
  const logoD = 42 * 0.8; // 20% smaller than the box's original fit
  placeLogo(cx - logoD / 2, boxY + (boxH - logoD) / 2, logoD, WHITE);

  // school name (navy) + motto (gold) on white, below the box
  setText(pdf, BLUE);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(18);
  pdf.text(SCHOOL.name, cx, boxY + boxH + 12, { align: 'center', maxWidth: A5_W - 24 });
  setText(pdf, GOLD);
  pdf.setFont(FONT, 'italic');
  pdf.setFontSize(9.5);
  pdf.text(`${SCHOOL.motto} — ${SCHOOL.mottoTranslation}`, cx, boxY + boxH + 20, {
    align: 'center',
  });

  // "PROGRESS REPORT"
  setText(pdf, INK);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(15);
  pdf.text('STUDENT PROGRESS REPORT', cx, 116, { align: 'center' });
  setDraw(pdf, GOLD);
  pdf.setLineWidth(0.5);
  pdf.line(40, 121, A5_W - 40, 121);

  // student name
  setText(pdf, BLUE);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(16);
  pdf.text(s.name, cx, 134, { align: 'center', maxWidth: A5_W - 30 });

  // detail grid — thin bordered, Rainbow-style, no fill
  const gx = 22;
  const gw = A5_W - 44;
  const gy = 141;
  const rowH = 8.5;
  // The grid shows the Year in the row above, so the reopen cell carries only
  // the day + month (e.g. "14 September") — drop any trailing 4-digit year.
  const reopenFull =
    (opt.back ?? DEFAULT_BACK_PAGE).schoolReopens ||
    (opt.getSchoolReopens?.(s.term) ?? schoolReopens(s.term)) ||
    '';
  const reopenDayMonth = reopenFull.replace(/,?\s*\d{4}\b\.?$/, '').trim() || '—';

  // Attendance — present / total school days for the term, from the assembled
  // register data (daysPresent/possibleDays). '—' when no register was taken.
  const attTotal = s.possibleDays ?? 0;
  const attPresent = s.daysPresent ?? (attTotal ? attTotal - (s.daysAbsent ?? 0) : 0);
  const attValue = attTotal > 0 ? `${attPresent} / ${attTotal}` : '—';
  const attPct = attTotal > 0 ? `${Math.round((attPresent / attTotal) * 100)}%` : '—';

  const rows: Array<[string, string, string, string]> = [
    [
      'Date of Birth',
      s.dateOfBirth || '—',
      'Student ID',
      s.admissionNumber || '—',
    ],
    ['Class', s.className, 'Year', s.year],
    [
      'Report for',
      s.juneCohort ? 'Term 1' : termLabel(s.term),
      'Reopens',
      reopenDayMonth,
    ],
    ['Days Present', attValue, 'Attendance', attPct],
  ];
  setDraw(pdf, RULE);
  pdf.setLineWidth(0.3);
  rows.forEach((r, i) => {
    const y = gy + i * rowH;
    pdf.rect(gx, y, gw, rowH, 'S');
    pdf.line(gx + gw / 2, y, gx + gw / 2, y + rowH);
    setText(pdf, GREY);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(7);
    pdf.text(r[0].toUpperCase(), gx + 2.5, y + 5.7);
    pdf.text(r[2].toUpperCase(), gx + gw / 2 + 2.5, y + 5.7);
    setText(pdf, INK);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(9);
    pdf.text(r[1], gx + 28, y + 5.9);
    pdf.text(r[3], gx + gw / 2 + 24, y + 5.9);
  });

  // footer: contact as plain text + gold hairline (no band)
  setDraw(pdf, GOLD);
  pdf.setLineWidth(1.2);
  pdf.line(14, A5_H - 22, A5_W - 14, A5_H - 22);
  setText(pdf, GREY);
  pdf.setFont(FONT, 'normal');
  pdf.setFontSize(7);
  pdf.text(SCHOOL.contact.addressLines.join(', '), cx, A5_H - 15, {
    align: 'center',
  });
  pdf.text(
    `${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`,
    cx,
    A5_H - 10,
    { align: 'center' },
  );

  // STEP 3: optional front-page notice. The space between the detail
  // grid (ends ~y=171) and the footer rule (y=A5_H-22 = 188) is
  // narrow — ~14mm. We render a small bordered block here ONLY when
  // the admin has configured a non-empty front-page notice for this
  // student's year+term. Silent absence is the default.
  const notice = opt.getReportNotice?.(s.year, s.term);
  if (notice && notice.frontPage.length > 0) {
    const nx = gx;
    const nw = gw;
    const ny = 176; // just below the detail grid
    const nh = 11; // ~2 lines at 7pt
    setDraw(pdf, GOLD);
    pdf.setLineWidth(0.4);
    pdf.rect(nx, ny, nw, nh, 'S');
    setText(pdf, GREY);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(6.5);
    pdf.text('NOTICE', nx + 2.5, ny + 4);
    setText(pdf, INK);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(7);
    // limit to 2 lines so we never overflow the footer
    const lines = pdf
      .splitTextToSize(notice.frontPage, nw - 16)
      .slice(0, 2);
    pdf.text(lines, nx + 14, ny + 4);
  }
}

/* page header/footer for inner pages — text + rule only, no fill */
function innerChrome(pdf: jsPDF, s: ReportStudent, pageNo: string) {
  const m = 12;
  // Two-tone accent strip across the very top (blue 62% / gold 38%) — a light,
  // toner-cheap design motif borrowed from the sister-school report.
  setFill(pdf, BLUE);
  pdf.rect(0, 0, A5_W * 0.62, 1.6, 'F');
  setFill(pdf, GOLD);
  pdf.rect(A5_W * 0.62, 0, A5_W * 0.38, 1.6, 'F');
  setText(pdf, BLUE);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(9);
  pdf.text(SCHOOL.shortName, m, 12);
  setText(pdf, GREY);
  pdf.setFont(FONT, 'normal');
  pdf.setFontSize(8);
  pdf.text(`${s.name} · ${s.className}`, A5_W - m, 12, {
    align: 'right',
  });
  setDraw(pdf, GOLD);
  pdf.setLineWidth(0.6);
  pdf.line(m, 15, A5_W - m, 15);

  setText(pdf, GREY);
  pdf.setFont(FONT, 'italic');
  pdf.setFontSize(6.5);
  pdf.text(SCHOOL.motto, m, A5_H - 7);
  pdf.text(pageNo, A5_W - m, A5_H - 7, { align: 'right' });
}

/* ---------------- pages 2-3 : inner academic ---------------- */
function innerSpread(
  pdf: jsPDF,
  s: ReportStudent,
  examTitleHeader: string,
): { page: number; y: number } {
  const m = 12;
  const w = A5_W - m * 2;
  const form = getFormLevel(s.className);
  const passMark = getPassMark(form);
  const showGrades = shouldShowGrades(form);
  const usesExam = s.juneCohort ? false : termUsesExam(s.term);
  const n = s.subjects.length;

  // Larger, readable fonts. Subjects flow across the two inner pages
  // (2 -> 3); the Class Teacher + HOY comments and the summary always
  // FOLLOW the subjects. For few-subject students everything fits with
  // room to spare, so the remarks naturally land on page 3; for heavy
  // Forms the subjects spill onto page 3 and the remarks follow them.
  // No comment is ever trimmed. Only a true two-inner-page overflow
  // (very rare) grows the booklet to the next multiple of 4.
  const lite = n <= 6;
  const dense = n >= 10; // heavy forms (e.g. Form 2 with ~12 subjects)
  const subjFs = lite ? 11 : dense ? 9.5 : 10;
  const teachFs = lite ? 8.5 : dense ? 7 : 7.5;
  const markFs = lite ? 11 : dense ? 9.5 : 10;
  const cmtFs = dense ? 8.5 : 9.5; // match the HOY / class-teacher remark size
  const headH = lite ? 9 : dense ? 7 : 8;
  const lineH = dense ? 3.6 : 4.0; // line pitch to suit the larger comment text
  const gap = lite ? 3 : dense ? 1.6 : 2.2;

  let page = 1; // becomes 2 on first addPage below
  const newInnerPage = (title: string) => {
    pdf.addPage();
    page += 1;
    innerChrome(pdf, s, `Page ${page}`);
    let yy = 22;
    // Two-tone accent tab (blue over gold) to the left of the section title.
    setFill(pdf, BLUE);
    pdf.rect(m, yy - 4.4, 2.4, 4.2, 'F');
    setFill(pdf, GOLD);
    pdf.rect(m, yy - 0.4, 2.4, 1.6, 'F');
    setText(pdf, BLUE);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(13);
    pdf.text(title, m + 5, yy);
    setText(pdf, GREY);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(8.5);
    pdf.text(`${s.juneCohort ? 'Term 1' : termLabel(s.term)} ${s.year}`, A5_W - m, yy, {
      align: 'right',
    });
    return yy + 6;
  };

  let y = newInnerPage('ACADEMIC RECORD'); // PAGE 2
  const marks: number[] = [];

  const ensure = (need: number, contTitle: string) => {
    if (y + need > A5_H - 12) {
      innerChrome(pdf, s, `Page ${page}`);
      y = newInnerPage(contTitle);
    }
  };

  // Split a teacher credit into per-teacher lines. Co-taught subjects list
  // several teachers separated by "/ , ; &"; drawn as one line they run
  // straight across the mark columns (13.5). Break on those delimiters, then
  // wrap each part to the column width as a fallback for a long single name.
  const teacherCreditLines = (text: string, maxW: number): string[] => {
    const parts = String(text).split(/\s*[/,;&]\s*/).map((t) => t.trim()).filter(Boolean);
    const out: string[] = [];
    for (const p of (parts.length ? parts : [String(text)])) {
      (pdf.splitTextToSize(p, maxW) as string[]).forEach((l) => out.push(l));
    }
    return out;
  };
  s.subjects.forEach((sub) => {
    // Right-hand mark columns are CENTRED on fixed x-anchors; the subject name
    // + teacher fill the left. Column centres (right → left): GRADE, EXAM, CA.
    const colW = 15;
    const gradeCx = m + w - 8;
    const examCx = usesExam ? gradeCx - colW : gradeCx;
    const firstCx = usesExam ? examCx - colW : gradeCx - colW; // CA / MARK centre
    const colsLeft = firstCx - colW / 2; // left edge of the mark columns
    const cTeach = m + 44;
    const teachW = Math.max(12, colsLeft - cTeach - 3);
    const nameW = Math.max(24, cTeach - (m + 2.5) - 2);

    // Measure the header: the subject name may wrap and the teacher credit may
    // wrap; the header band grows to the tallest of the two so nothing ever
    // bleeds into the comment paragraph below.
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(subjFs);
    const nameLines = pdf.splitTextToSize(sub.name, nameW) as string[];
    const nameLineH = subjFs * 0.42;
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(teachFs);
    const teachLines = sub.teacher ? teacherCreditLines(sub.teacher, teachW) : [];
    const teachLineH = teachFs * 0.42;

    pdf.setFontSize(cmtFs);
    const cmt = pdf.splitTextToSize(sub.comment || '—', w - 6) as string[];
    const bodyH = Math.max(7, cmt.length * lineH + 3.5);
    const nameBlockH = 2.8 + nameLines.length * nameLineH;
    const teachBlockH = 2.8 + teachLines.length * teachLineH;
    const hdH = Math.max(headH, nameBlockH, teachBlockH);
    const boxH = hdH + bodyH;
    ensure(boxH + gap, 'ACADEMIC RECORD (continued)');

    // Soft rounded card with a tinted header band (rounded to match).
    setDraw(pdf, RULE);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(m, y, w, boxH, 1.8, 1.8, 'S');
    setFill(pdf, FAINT);
    pdf.roundedRect(m + 0.3, y + 0.3, w - 0.6, hdH, 1.5, 1.5, 'F');
    setDraw(pdf, RULE);
    pdf.setLineWidth(0.2);
    pdf.line(m, y + hdH, m + w, y + hdH);

    // Subject name (top-aligned, wraps within nameW so it never hits teacher).
    setText(pdf, BLUE);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(subjFs);
    nameLines.forEach((ln, i) => pdf.text(ln, m + 2.5, y + 4 + i * nameLineH));
    // Teacher credit, aligned to the first name line.
    setText(pdf, GREY);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(teachFs);
    teachLines.forEach((tl, i) => pdf.text(tl, cTeach, y + 4 + i * teachLineH));

    // Column titles (top) + values (below), both CENTRED on each anchor.
    const labelY = y + 2.2;
    const valY = y + 6.4;
    setText(pdf, GREY);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(4.8);
    if (usesExam) {
      pdf.text('CA', firstCx, labelY, { align: 'center' });
      pdf.text(examTitleHeader, examCx, labelY, { align: 'center' });
    } else {
      pdf.text('MARK', firstCx, labelY, { align: 'center' });
    }
    if (showGrades) pdf.text('GRADE', gradeCx, labelY, { align: 'center' });

    const gradeMark = usesExam ? sub.exam : sub.termMark;
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(markFs);
    if (usesExam) {
      setText(pdf, INK);
      pdf.text(sub.caMark === null ? '–' : String(sub.caMark), firstCx, valY, { align: 'center' });
      setText(pdf, typeof sub.exam === 'number' && sub.exam < passMark ? [193, 53, 53] : INK);
      pdf.text(
        sub.exam === null ? '–' : isAbsent(sub.exam) ? 'ABS' : String(sub.exam),
        examCx, valY, { align: 'center' },
      );
    } else {
      setText(pdf, typeof sub.termMark === 'number' && sub.termMark < passMark ? [193, 53, 53] : INK);
      pdf.text(sub.termMark === null ? '–' : String(sub.termMark), firstCx, valY, { align: 'center' });
    }
    if (showGrades) {
      const g = sub.grade || '-';
      if (g === '-') {
        setText(pdf, GREY);
        pdf.text(g, gradeCx, valY, { align: 'center' });
      } else {
        // Grade box: SQUARE edges, filled, white letter — centred on the grade
        // column so the 'GRADE' title sits directly above it.
        const gc = gradeColor(g);
        const cw = 7.6;
        const ch = 5.0;
        const gx = gradeCx - cw / 2;
        const gy = y + 2.8;
        setFill(pdf, [gc[0], gc[1], gc[2]]);
        pdf.rect(gx, gy, cw, ch, 'F');
        setText(pdf, [255, 255, 255]);
        pdf.setFont(FONT, 'bold');
        pdf.setFontSize(markFs - 1);
        pdf.text(g, gradeCx, gy + ch * 0.72, { align: 'center' });
      }
    }

    // Comment paragraph, below the (possibly grown) header band.
    setText(pdf, INK);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(cmtFs);
    pdf.text(cmt, m + 2.5, y + hdH + 4);

    if (typeof gradeMark === 'number') marks.push(gradeMark);
    y += boxH + gap;
  });

  // Measure both remark boxes up front so the Class-Teacher and Head-of-Year
  // comments ALWAYS stay together on one page — never split across a page
  // break, even if the Class-Teacher box alone would have fit below the
  // subjects here.
  const remFs = dense ? 8.5 : 9.5;
  const rh = dense ? 7.5 : 9;
  const gapAfter = dense ? 3.5 : 5;
  const remarkBoxH = (text: string): number => {
    pdf.setFontSize(remFs);
    const lines = pdf.splitTextToSize(text || '—', w - 6) as string[];
    const rbody = Math.max(dense ? 11 : 14, lines.length * (dense ? 4 : 4.4) + (dense ? 5 : 6));
    return rh + rbody;
  };
  const ctH = remarkBoxH(s.classTeacherComment);
  const hoyH = remarkBoxH(s.hodComment);
  const bothH = ctH + gapAfter + hoyH + gapAfter;

  // RULE: the Class-Teacher + HOY comments live on ONE page. Light students
  // get a fresh page 3 (the heading is the divider). Heavier Forms keep the
  // remarks below the subjects ONLY if BOTH boxes fit there; otherwise the
  // whole pair breaks to a fresh page so they always stay together.
  const labelBlock = 15; // pre-gap + divider label height
  if (page < 3 || y + labelBlock + bothH > A5_H - 12) {
    innerChrome(pdf, s, `Page ${page}`);
    y = newInnerPage('TEACHER REMARKS');
  } else {
    y += 3;
    setDraw(pdf, GOLDLINE);
    pdf.setLineWidth(0.7);
    pdf.line(m, y, m + w, y);
    setText(pdf, BLUE);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(11);
    const lbl = 'TEACHER REMARKS';
    const lblW = pdf.getTextWidth(lbl) + 6;
    setFill(pdf, GOLDTINT);
    pdf.rect(m, y + 1.5, lblW, 7, 'F');
    setText(pdf, BLUE);
    pdf.text(lbl, m + 3, y + 6.6);
    y += 12;
  }

  const remark = (title: string, text: string) => {
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(remFs);
    const lines = pdf.splitTextToSize(text || '—', w - 6) as string[];
    const rboxH = remarkBoxH(text);

    // remark boxes are visually a DIFFERENT class of box from the
    // subject boxes: gold-tinted header strip + gold top border so
    // a parent's eye registers "this is the overall verdict".
    setDraw(pdf, RULE);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(m, y, w, rboxH, 1.8, 1.8, 'S');
    setFill(pdf, GOLDTINT);
    pdf.roundedRect(m + 0.3, y + 0.3, w - 0.6, rh, 1.5, 1.5, 'F');
    setDraw(pdf, GOLDLINE);
    pdf.setLineWidth(0.5);
    pdf.line(m, y + rh, m + w, y + rh); // gold strip underline
    setText(pdf, BLUE);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(dense ? 9 : 10);
    pdf.text(title, m + 2, y + (dense ? 5 : 6));
    setText(pdf, INK);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(remFs);
    pdf.text(lines, m + 2, y + rh + (dense ? 4 : 5));
    y += rboxH + gapAfter;
  };
  remark(
    `Class Teacher${s.classTeacherName ? ' · ' + s.classTeacherName : ''} — Attendance & Conduct`,
    s.classTeacherComment,
  );
  remark(
    `Head of Year${s.hodName ? ' · ' + s.hodName : ''} — on behalf of the Principal`,
    s.hodComment,
  );

  const avg =
    marks.length > 0 ? Math.round(marks.reduce((a, b) => a + b, 0) / marks.length) : 0;
  const failed = marks.filter((mk) => mk < passMark).length;
  ensure(22, 'SUMMARY');

  // Results strip: a light band with a blue left-accent carrying the headline
  // stats (Average · Subjects failed · Days absent) and, in Term 3, a coloured
  // PASS/FAIL pill — a sister-school cue that pulls the verdict together.
  const stripH = 12;
  setFill(pdf, FAINT);
  pdf.roundedRect(m, y, w, stripH, 1.8, 1.8, 'F');
  setFill(pdf, BLUE);
  pdf.roundedRect(m, y, 2.6, stripH, 1.3, 1.3, 'F');

  const stat = (label: string, value: string, x: number, valColor: [number, number, number]) => {
    setText(pdf, GREY);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(6.2);
    pdf.text(label.toUpperCase(), x, y + 4);
    setText(pdf, valColor);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(11);
    pdf.text(value, x, y + 9.4);
  };
  stat('Average', `${avg}%`, m + 6, INK);
  stat('Subjects failed', String(failed), m + 40, failed > 0 ? [193, 53, 53] : [22, 130, 70]);
  if (s.daysAbsent !== undefined) {
    stat('Days absent', String(s.daysAbsent), m + 80, s.daysAbsent > 5 ? [193, 53, 53] : INK);
  }
  if (s.term === 'Term3') {
    const pass = failed < 2;
    const pillW = 22;
    const pillH = 7;
    const px = m + w - pillW - 3;
    const py = y + (stripH - pillH) / 2;
    setFill(pdf, pass ? [220, 244, 228] : [250, 224, 224]);
    pdf.roundedRect(px, py, pillW, pillH, 1.6, 1.6, 'F');
    setText(pdf, pass ? [22, 130, 70] : [193, 53, 53]);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(10);
    pdf.text(pass ? 'PASS' : 'FAIL', px + pillW / 2, py + 4.9, { align: 'center' });
  }
  y += stripH + 3;
  setText(pdf, GREY);
  pdf.setFont(FONT, 'normal');
  pdf.setFontSize(7.5);
  pdf.text(`Pass mark Form ${form ?? '?'}: ${passMark}%`, A5_W - m, y, {
    align: 'right',
  });
  innerChrome(pdf, s, `Page ${page}`);

  // Safety: if somehow only one inner page was used, pad so the
  // booklet still folds (cover/2/3/back = 4).
  if (page < 3) {
    pdf.addPage();
    page = 3;
    y = 22;
  }
  // Report the final inner page + y so renderStudentReport can decide whether
  // a full back cover (page ≤ 3) or a compact on-page footer (overflow to
  // page 4) keeps the booklet at 4 pages.
  return { page, y };
}

/* ---------------- page 4 : back (white, ink-light) ---------------- */
function backCover(
  pdf: jsPDF,
  s: ReportStudent,
  back: BackPageContent,
  backPageNotice: string,
  opt: RenderOptions = {},
) {
  pdf.addPage();
  const m = 12;
  const w = A5_W - m * 2;

  setDraw(pdf, GOLD);
  pdf.setLineWidth(1.2);
  pdf.line(m, 14, A5_W - m, 14);

  // STEP 3: pick the notice source — configured per-term override
  // wins, fallback to back.announcements (DEFAULT_BACK_PAGE has
  // generic text). If BOTH are empty, the announcements block is
  // skipped entirely — silent absence, no orphaned title — and the
  // term-dates panel rides up to fill the page.
  const noticeText =
    backPageNotice.length > 0
      ? backPageNotice
      : back.announcements;
  let y = 24; // baseline if no notice is drawn
  if (noticeText.length > 0) {
    setText(pdf, BLUE);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(13);
    pdf.text(back.announcementsTitle.toUpperCase(), m, 24);

    setText(pdf, INK);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(9);
    const ann = pdf.splitTextToSize(noticeText, w);
    pdf.text(ann, m, 33);
    y = 33 + ann.length * 4.5 + 8;
  }

  // term dates — thin bordered box
  setDraw(pdf, RULE);
  pdf.setLineWidth(0.3);
  const tdH = 26;
  pdf.rect(m, y, w, tdH, 'S');
  setText(pdf, BLUE);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(9);
  pdf.text('TERM DATES', m + 3, y + 7);
  setText(pdf, INK);
  pdf.setFont(FONT, 'normal');
  pdf.setFontSize(8.5);
  pdf.text(
    // June cohort's Term 1 is out of cycle — don't print the January Term-1
    // calendar dates for them (they'd be misleading); just the term + year.
    s.juneCohort
      ? `Term 1 ${s.year}`
      : `${termLabel(s.term)} ${s.year}:  ${opt.getTermDates?.(s.term) ?? termDates(s.term)}`,
    m + 3,
    y + 15,
  );
  // Prefer an explicit back-page value; otherwise the admin Term Calendar,
  // then the configured reopen date — so the line is never blank.
  const reopen = back.schoolReopens || (opt.getSchoolReopens?.(s.term) ?? schoolReopens(s.term));
  if (reopen) {
    pdf.setFont(FONT, 'bold');
    pdf.text(
      `School reopens:  ${reopen}`,
      m + 3,
      y + 22,
    );
  }
  y += tdH + 9;

  // grade key — thin-bordered table, blue header text (no fill)
  setText(pdf, BLUE);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(9);
  pdf.text('GRADE KEY', m, y);
  y += 4;
  const rowH = 7;
  const c1 = m + 14;
  const c2 = m + 42;
  setDraw(pdf, RULE);
  pdf.setLineWidth(0.3);
  // header
  pdf.rect(m, y, w, rowH, 'S');
  setText(pdf, GREY);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(7);
  pdf.text('GRADE', m + 2, y + 4.7);
  pdf.text('RANGE', c1 + 2, y + 4.7);
  pdf.text('DESCRIPTION', c2 + 2, y + 4.7);
  y += rowH;
  const desc: Record<string, string> = {
    'A*': 'Exceptional performance',
    A: 'Outstanding performance',
    B: 'Good performance',
    C: 'Working at expected level',
    D: 'Working towards expected level',
    E: 'Working below expected level',
    F: 'Working well below expected level',
    G: 'Working far below expected level',
    U: 'Working profoundly below level',
  };
  const drawKeyRow = (b: { grade: string; min: number; max: number }) => {
    pdf.rect(m, y, w, rowH, 'S');
    pdf.line(c1, y, c1, y + rowH);
    pdf.line(c2, y, c2, y + rowH);
    const gc = gradeColor(b.grade);
    setText(pdf, [gc[0], gc[1], gc[2]]);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(8);
    pdf.text(b.grade, m + 3, y + 4.8);
    setText(pdf, INK);
    pdf.setFont(FONT, 'normal');
    pdf.text(`${b.min}–${b.max}%`, c1 + 2, y + 4.8);
    setText(pdf, GREY);
    pdf.text(desc[b.grade] ?? '', c2 + 2, y + 4.8);
    y += rowH;
  };
  // Extended key (unchanged for all-extended cards).
  GRADE_BOUNDARIES.forEach(drawKeyRow);
  // Core-only grades (F, G) are appended ONLY when this card has a Core-tier
  // subject, so extended-only report cards are byte-identical. A caption
  // resolves the C-range ambiguity (Core C spans 60-100, capped at C).
  const hasCore = s.subjects.some((r) => r.scheme === 'core');
  if (hasCore) {
    const extendedGrades = new Set(GRADE_BOUNDARIES.map((b) => b.grade));
    CORE_GRADE_BOUNDARIES.filter((b) => !extendedGrades.has(b.grade)).forEach(
      drawKeyRow,
    );
    y += 3;
    setText(pdf, GREY);
    pdf.setFont(FONT, 'italic');
    pdf.setFontSize(6.8);
    pdf.text(
      pdf.splitTextToSize(
        'Core-tier subjects are graded C-G and capped at grade C (a mark of 60% or above grades C). F and G apply to Core subjects only.',
        w,
      ),
      m,
      y,
    );
    y += 8;
  }

  if (back.extra) {
    y += 8;
    setText(pdf, GREY);
    pdf.setFont(FONT, 'italic');
    pdf.setFontSize(8.5);
    pdf.text(pdf.splitTextToSize(back.extra, w), m, y);
  }

  // footer
  setDraw(pdf, GOLD);
  pdf.setLineWidth(1.2);
  pdf.line(m, A5_H - 18, A5_W - m, A5_H - 18);
  setText(pdf, GREY);
  pdf.setFont(FONT, 'normal');
  pdf.setFontSize(7);
  pdf.text(SCHOOL.name, A5_W / 2, A5_H - 11, { align: 'center' });
  pdf.text(
    `${SCHOOL.contact.website}  ·  ${SCHOOL.contact.email}`,
    A5_W / 2,
    A5_H - 6,
    { align: 'center' },
  );
}

/* Condensed HORIZONTAL grade key (Al-Nur style): a run of small colour chips
 * with their ranges, wrapping to a 2nd row if a Core-tier F/G is present.
 * Returns the y below the last row. */
function drawHorizontalGradeKey(
  pdf: jsPDF,
  s: ReportStudent,
  x: number,
  yTop: number,
  w: number,
): number {
  const base = yTop + 3;
  setText(pdf, GREY);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(7);
  pdf.text('GRADE KEY', x, base);

  const bounds: { grade: string; min: number; max: number }[] = GRADE_BOUNDARIES.map(
    (b) => ({ grade: b.grade, min: b.min, max: b.max }),
  );
  const hasCore = s.subjects.some((r) => r.scheme === 'core');
  if (hasCore) {
    const ext = new Set(GRADE_BOUNDARIES.map((b) => b.grade));
    CORE_GRADE_BOUNDARIES.filter((b) => !ext.has(b.grade)).forEach((b) =>
      bounds.push({ grade: b.grade, min: b.min, max: b.max }),
    );
  }

  const startX = x + 20;
  const cellW = 17.5;
  const perRow = Math.max(1, Math.floor((w - 20) / cellW));
  let ky = base;
  bounds.forEach((b, i) => {
    const col = i % perRow;
    if (i > 0 && col === 0) ky += 6;
    const kx = startX + col * cellW;
    const gc = gradeColor(b.grade);
    setFill(pdf, [gc[0], gc[1], gc[2]]);
    pdf.rect(kx, ky - 3, 6.5, 4, 'F');
    setText(pdf, WHITE);
    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(6);
    pdf.text(b.grade, kx + 3.25, ky, { align: 'center' });
    setText(pdf, GREY);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(6.2);
    pdf.text(`${b.min}-${b.max}`, kx + 7.8, ky);
  });
  return ky + 3;
}

/* Compact footer drawn on the CURRENT (overflow) page instead of a separate
 * back cover — used when the academic content + remarks spilled onto page 4.
 * Keeps the booklet at 4 pages: term dates + a condensed horizontal grade key
 * fill the space below the remarks. No new page is added. */
function compactBack(
  pdf: jsPDF,
  s: ReportStudent,
  yStart: number,
  opt: RenderOptions,
): void {
  const m = 12;
  const w = A5_W - m * 2;
  const form = getFormLevel(s.className);
  const showGrades = shouldShowGrades(form);

  // Reserve the block height up front and BOTTOM-ANCHOR it so the condensed
  // grade key can never be pushed off the page by long remarks. The grade key
  // is the priority; term dates ride just above it. (Reopen date lives on the
  // cover, so it's dropped here to save space.)
  const keyRows = showGrades
    ? Math.ceil(
        (GRADE_BOUNDARIES.length +
          (s.subjects.some((r) => r.scheme === 'core') ? 2 : 0)) /
          Math.max(1, Math.floor((w - 20) / 17.5)),
      )
    : 0;
  const keyBlockH = showGrades ? 3 + keyRows * 6 + 5 : 0; // label + rows + fail note
  const blockH = 6 + 5 + keyBlockH; // divider gap + term-dates line + key
  const bottomAnchor = A5_H - 10 - blockH; // never overlaps the page footer
  let y = Math.min(Math.max(yStart + 5, 112), Math.max(112, bottomAnchor));

  setDraw(pdf, GOLDLINE);
  pdf.setLineWidth(0.6);
  pdf.line(m, y, m + w, y);
  y += 6;

  // Term dates (compact, one line)
  setText(pdf, BLUE);
  pdf.setFont(FONT, 'bold');
  pdf.setFontSize(8);
  pdf.text('TERM DATES', m, y);
  setText(pdf, INK);
  pdf.setFont(FONT, 'normal');
  pdf.setFontSize(8);
  pdf.text(
    s.juneCohort
      ? `Term 1 ${s.year}`
      : `${termLabel(s.term)} ${s.year}:  ${opt.getTermDates?.(s.term) ?? termDates(s.term)}`,
    m + 26,
    y,
  );
  y += 5;

  if (showGrades) {
    y = drawHorizontalGradeKey(pdf, s, m, y, w);
    y += 2;
    const passMark = getPassMark(form);
    setText(pdf, GREY);
    pdf.setFont(FONT, 'normal');
    pdf.setFontSize(6.8);
    pdf.text('Marks in', m, y);
    setText(pdf, [193, 53, 53]);
    pdf.setFont(FONT, 'bold');
    pdf.text('RED', m + 11, y);
    setText(pdf, GREY);
    pdf.setFont(FONT, 'normal');
    pdf.text(`indicate a mark below the ${passMark}% pass mark.`, m + 20, y);
  }
}

export function renderStudentReport(
  pdf: jsPDF,
  s: ReportStudent,
  isFirst: boolean,
  opt: RenderOptions = {},
): void {
  FONT = registerReportFont(pdf);
  const back = opt.back ?? DEFAULT_BACK_PAGE;
  if (!isFirst) pdf.addPage();

  const coverPageIndex = pdf.getNumberOfPages();
  cover(pdf, s, opt); // booklet page 1
  // Resolve the exam header text now so all downstream rendering
  // sees the same value. Uppercased to match the existing style
  // (header was previously the hardcoded 'EXAM').
  const formLevel = getFormLevel(s.className);
  const resolved =
    opt.getExamTitle?.(formLevel, s.term) ?? undefined;
  const examTitleHeader = (resolved ?? 'Exam').toUpperCase();
  const end = innerSpread(pdf, s, examTitleHeader); // pages 2 (+3, +4 on overflow)

  void coverPageIndex;
  // STEP 3: resolve back-page notice from the same callback that
  // fed the cover. Empty string -> backCover skips the block.
  const resolvedNotice = opt.getReportNotice?.(s.year, s.term);
  const backPageNotice = resolvedNotice?.backPage ?? '';

  // DYNAMIC 4-PAGE RULE: if the academic content + remarks fit within pages
  // 2–3, add a full back cover (grade-key table, announcements, term dates) as
  // page 4. If they OVERFLOWED onto page 4, don't add a 5th page — draw a
  // compact footer (term dates + condensed horizontal grade key) below the
  // remarks so the booklet stays at 4 pages.
  if (end.page <= 3) {
    backCover(pdf, s, back, backPageNotice, opt); // full back = page 4
  } else {
    compactBack(pdf, s, end.y, opt); // fills the rest of page 4
  }
}

export function buildClassReportPdf(
  students: ReportStudent[],
  opt: RenderOptions = {},
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: [A5_W, A5_H] });
  students.forEach((s, i) => renderStudentReport(pdf, s, i === 0, opt));
  return pdf;
}

export function buildSingleReportPdf(
  s: ReportStudent,
  opt: RenderOptions = {},
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: [A5_W, A5_H] });
  renderStudentReport(pdf, s, true, opt);
  return pdf;
}

/**
 * Academic pages ONLY — the inner spread (marks / grades / bars / remarks /
 * summary), with no cover and no back cover. Powers the in-app "live view"
 * every teacher gets in View Reports as marks are entered (privacy: teachers
 * see the academic record, never the branded cover/back booklet). Because
 * `innerSpread`'s first act is `addPage()`, page 1 stays blank on a fresh
 * doc — we drop it so the preview opens straight on the marks.
 */
export function buildAcademicPreviewPdf(
  s: ReportStudent,
  opt: RenderOptions = {},
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: [A5_W, A5_H] });
  FONT = registerReportFont(pdf);
  const formLevel = getFormLevel(s.className);
  const resolved = opt.getExamTitle?.(formLevel, s.term) ?? undefined;
  const examTitleHeader = (resolved ?? 'Exam').toUpperCase();
  innerSpread(pdf, s, examTitleHeader);
  if (pdf.getNumberOfPages() > 1) pdf.deletePage(1);
  return pdf;
}
